import React, { useState } from 'react';
import { observer } from 'mobx-react';
import MarkdownContent from '../CompanyPage/MarkdownContent';
import EditWrapper from '../CompanyPage/EditWrapper';
import CompanyOfferings from '../../components/CompanyOfferings/CompanyOfferings';
import ItemSwitcher from '../components/ItemSwitcher';
import useQueryParam from '../components/useQueryParam';
import { FileDownloader } from '../../FileDownloader';
import useUnsavedGuard from '../components/useUnsavedGuard';
import {
  PageWrapper, PageHeader, PageTitle, EditButton,
  DownloadLink, EmptyState, SubCard, SubCardTitle, PageSkeleton,
} from '../CompanyPage/CompanyPage.styles';

const SECTION_LABELS = [
  { key: 'differentiator', label: 'Differentiators' },
];

const hasDocument = (offering) => !!(offering.uploadLink || offering.uploadKey);

// The line under a name in the rail / accordion, only when it says something
// the name does not: a document is attached, or nothing has been written yet.
// Every offering has differentiators, so naming them under each one was noise.
export const captionFor = (offering) => {
  if (!offering.differentiator && !hasDocument(offering)) return 'No details yet';
  return hasDocument(offering) ? 'Includes a document' : null;
};

const OfferingsPage = observer(({ company, mode }) => {
  const [editing, setEditing] = useState(false);
  const { onDirtyChange, confirmLeave } = useUnsavedGuard();
  // The selected offering lives in the URL (?item=2, 1-based): a refresh keeps
  // it, the back button steps through selections, the address can be shared.
  const [itemParam, setItemParam] = useQueryParam('item', '1');
  const isReadonly = mode === 'readonly';
  const loaded = !!company?.id;
  const offerings = company?.offerings || [];

  if (editing) {
    return (
      <PageWrapper>
        <PageHeader>
          <PageTitle>Offerings</PageTitle>
        </PageHeader>
        <EditWrapper onBack={() => { if (confirmLeave()) setEditing(false); }}>
          <CompanyOfferings company={company} onDirtyChange={onDirtyChange} />
        </EditWrapper>
      </PageWrapper>
    );
  }

  // Every offering stays visible by name at every width (see ItemSwitcher);
  // the old one-line tab bar hid the overflow behind a hidden scrollbar.
  const items = offerings.map((offering, i) => ({
    key: String(i),
    label: offering.offering || `Offering #${i + 1}`,
    caption: captionFor(offering),
  }));
  const requested = parseInt(itemParam, 10);
  const safeIndex = Number.isInteger(requested) && requested >= 1 && requested <= offerings.length ? requested - 1 : 0;

  const renderDetail = (item, { index }) => {
    const offering = offerings[index];
    if (!offering) return null;
    return (
      <>
        {SECTION_LABELS.map(({ key, label }) => {
          const content = offering[key];
          if (!content) return null;
          return (
            <SubCard key={key}>
              <SubCardTitle>{label}</SubCardTitle>
              <MarkdownContent markdown={content} />
            </SubCard>
          );
        })}

        {hasDocument(offering) && (
          <div>
            {offering.uploadLink ? (
              <DownloadLink href={offering.uploadLink} target="_blank" rel="noopener noreferrer">
                {'\u{1F4CE}'} Download Document
              </DownloadLink>
            ) : (
              <DownloadLink as="span" style={{ cursor: 'pointer' }} onClick={() => FileDownloader(offering.uploadKey)}>
                {'\u{1F4CE}'} {offering.uploadKey.split('/').pop()}
              </DownloadLink>
            )}
          </div>
        )}
      </>
    );
  };

  return (
    <PageWrapper data-tour="offerings-page">
      <PageHeader>
        <PageTitle>Offerings</PageTitle>
        {loaded && !isReadonly && (
          <EditButton onClick={() => setEditing(true)}>
            {'✎'} Edit
          </EditButton>
        )}
      </PageHeader>

      {!loaded ? (
        <PageSkeleton>
          <div style={{ flex: '0 0 280px', height: 220 }} />
          <div style={{ flex: 1, height: 320 }} />
        </PageSkeleton>
      ) : offerings.length === 0 ? (
        <EmptyState>No offerings have been added yet.</EmptyState>
      ) : (
        <ItemSwitcher
          items={items}
          activeKey={String(safeIndex)}
          onChange={(key) => setItemParam(String(Number(key) + 1))}
          renderDetail={renderDetail}
          ariaLabel="Offerings"
          countLabel="offerings"
        />
      )}
    </PageWrapper>
  );
});

export default OfferingsPage;
